import { useState, useEffect } from "react";
import { X, Phone, Mail, Calendar, Save, Trash2, History, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { getLead, updateStage, updateNote, deleteLead } from "@/lib/api";
import { toast } from "sonner";
import LeadActivities from "@/components/LeadActivities";
import SchedaCliente from "@/components/SchedaCliente";
import OutcomeDialog from "@/components/OutcomeDialog";
import { TierBadge } from "@/components/TierBadge";


const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function LeadDetail({ leadId, onClose, onUpdated, stages }) {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [pendingStage, setPendingStage] = useState(null);

  useEffect(() => {
    getLead(leadId).then((l) => { setLead(l); setNote(l.note_consulente || ""); });
  }, [leadId]);

  const changeStage = async (stage, esitoFinale) => {
    if (!esitoFinale && (stage === "Erogato" || stage === "Perso")) { setPendingStage(stage); return; }
    const l = await updateStage(leadId, stage, esitoFinale);
    setLead(l); setPendingStage(null); onUpdated(); toast.success("Stage aggiornato");
  };
  const saveNote = async () => {
    await updateNote(leadId, note); toast.success("Nota salvata"); onUpdated();
  };
  const remove = async () => {
    await deleteLead(leadId); toast.success("Lead eliminato"); onUpdated(); onClose();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="lead-detail-drawer" className="w-full sm:max-w-lg overflow-y-auto overflow-x-hidden bg-slate-950 border-white/10 text-slate-100 p-0">
        <SheetTitle className="sr-only">Dettaglio lead</SheetTitle>
        {!lead ? (
          <div className="p-6 text-slate-400">Caricamento...</div>
        ) : (
          <div>
            <div className="sticky top-0 bg-slate-950 border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-display font-bold text-lg text-white flex items-center gap-2 flex-wrap">{lead.contatto?.nome || "Senza nome"}
                  <TierBadge tier={lead.tier} testid="lead-tier-badge" />
                  {lead.source === "acchiappo" && <span className="align-middle text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300" data-testid="lead-source-badge">Sito acchiappo</span>}
                </h3>
                <p className="text-xs text-slate-400">{lead.pipeline_stage}{lead.esito_finale?.importo ? ` · ${new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(lead.esito_finale.importo)}` : ""}{lead.esito_finale?.motivo ? ` · ${lead.esito_finale.motivo}` : ""}</p>
              </div>
              <Button data-testid="detail-close-button" variant="ghost" size="icon" onClick={onClose} className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Contatti */}
              <div className="grid grid-cols-1 gap-2 text-sm">
                <a href={`tel:${lead.contatto?.telefono}`} className="flex items-center gap-2 text-slate-300 hover:text-accent">
                  <Phone className="h-4 w-4" /> {lead.contatto?.telefono || "—"}
                </a>
                <a href={`mailto:${lead.contatto?.email}`} className="flex items-center gap-2 text-slate-300 hover:text-accent">
                  <Mail className="h-4 w-4" /> {lead.contatto?.email || "—"}
                </a>
                <div className="flex items-center gap-2 text-slate-400">
                  <Calendar className="h-4 w-4" /> Anno nascita: {lead.contatto?.anno_nascita || "—"}
                </div>
              </div>

              {/* Scheda cliente (schema domande filtro) */}
              <SchedaCliente lead={lead} />

              {/* Scores */}
              <div className="grid grid-cols-2 gap-3">
                <div data-testid="detail-score-cat" className="rounded-lg bg-slate-900 border border-white/10 p-4">
                  <div className="text-xs font-bold uppercase text-slate-400">Score categoriale</div>
                  <div className="font-mono font-bold text-3xl text-accent mt-1">{lead.score_categoriale}/10</div>
                  <div className="text-xs text-slate-500 mt-1">{lead.score_categoriale_label}</div>
                </div>
                <div data-testid="detail-score-ins" className="rounded-lg bg-slate-900 border border-white/10 p-4">
                  <div className="text-xs font-bold uppercase text-slate-400">Score assicurativo</div>
                  <div className="font-mono font-bold text-3xl text-emerald-400 mt-1">{lead.score_assicurativo}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase">{lead.esito_assicurativo}</div>
                </div>
              </div>

              {/* Rata */}
              <div className="rounded-lg bg-slate-900 border border-white/10 p-4 grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className={`font-mono font-bold text-base ${lead.eta > 75 ? "text-rose-400" : "text-white"}`} data-testid="lead-eta">{lead.eta ?? "—"}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Età</div>
                </div>
                <div>
                  <div className="font-mono font-bold text-base text-white">{fmt(lead.rata?.netto_stimato)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Netto</div>
                </div>
                <div>
                  <div className="font-mono font-bold text-base text-accent">{fmt(lead.rata?.rata_massima)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Rata max</div>
                </div>
                <div>
                  <div className="font-mono font-bold text-base text-emerald-400">{fmt(lead.rata?.residuo_netto)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Residuo</div>
                </div>
              </div>

              {/* Detrazioni */}
              {lead.detrazioni_assicurative?.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-slate-400 mb-2">Detrazioni assicurative</div>
                  <div className="space-y-1">
                    {lead.detrazioni_assicurative.map((d) => (
                      <div key={d.label} className="flex justify-between text-sm text-slate-300 bg-slate-900 rounded px-3 py-1.5">
                        <span>{d.label}</span><span className="font-mono text-rose-400">-{d.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KO reasons */}
              {lead.ko_reasons?.length > 0 && (
                <div className="rounded-lg bg-rose-950/40 border border-rose-900 p-4">
                  <div className="text-xs font-bold uppercase text-rose-400 mb-2">Motivi KO</div>
                  <ul className="space-y-1 text-sm text-rose-300">
                    {lead.ko_reasons.map((r) => <li key={r}>• {r}</li>)}
                  </ul>
                </div>
              )}

              {/* Finanziamenti + rinnovo */}
              {lead.rinnovi?.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-slate-400 mb-2">Finanziamenti in corso & rinnovi</div>
                  <div className="space-y-2">
                    {lead.rinnovi.map((f, i) => (
                      <div key={`${f.tipo}-${f.inizio || i}-${f.con_chi || ""}`} className="rounded-lg bg-slate-900 border border-white/10 p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-white">{f.tipo} · {f.con_chi || "n.d."}</span>
                          {f.rinnovabile ? (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-800">Rinnovabile</span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-800 text-slate-400">Non ancora</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                          {f.rata != null && <span>Rata: <span className="font-mono text-slate-200">{fmt(f.rata)}</span></span>}
                          {f.durata_mesi ? <span>Durata: {f.durata_mesi} mesi</span> : null}
                          {f.pagato_pct != null && <span>Pagato: {f.pagato_pct}%</span>}
                          {f.data_rinnovabilita && <span>Rinnovo da: {f.data_rinnovabilita}</span>}
                          {f.data_richiamo && <span className="text-accent col-span-2">Richiamo auto: {f.data_richiamo} (−4 mesi)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analisi azienda */}
              {lead.azienda_analysis && (
                <div className="rounded-lg bg-slate-900 border border-white/10 p-4">
                  <div className="text-xs font-bold uppercase text-slate-400 mb-2">Analisi azienda · {lead.azienda_analysis.fonte?.includes("InfoCamere") ? "Registro Imprese" : "stima AI"}</div>
                  {(lead.criticita_azienda?.length > 0) && (
                    <div data-testid="lead-criticita-azienda" className="mb-3 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                      <span className="font-bold uppercase">Da verificare:</span> {lead.criticita_azienda.join(" · ")}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-y-1 text-sm text-slate-300">
                    <span className="text-slate-500">Stato</span><span>{lead.azienda_analysis.stato_attivita || "n.d."}</span>
                    <span className="text-slate-500">Fatturato</span><span>{lead.azienda_analysis.fatturato_stimato}</span>
                    <span className="text-slate-500">Dipendenti</span><span>{lead.azienda_analysis.dipendenti_stimati}</span>
                    <span className="text-slate-500">Bilancio</span><span>{lead.azienda_analysis.giudizio_bilancio}</span>
                    <span className="text-slate-500">Cassa integr.</span><span>{lead.azienda_analysis.cassa_integrazione || "n.d."}</span>
                    <span className="text-slate-500">Procedure</span><span>{lead.azienda_analysis.procedure_concorsuali || lead.azienda_analysis.provvedimenti_noti}</span>
                    <span className="text-slate-500">Rischio</span><span className={lead.azienda_analysis.rischio === "alto" ? "text-rose-400 font-semibold" : ""}>{lead.azienda_analysis.rischio}</span>
                  </div>
                  {lead.azienda_analysis.sintesi_web && <p data-testid="lead-sintesi-web" className="text-xs text-sky-200 mt-2">{lead.azienda_analysis.sintesi_web}</p>}
                  {lead.azienda_analysis.fonti_web?.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {lead.azienda_analysis.fonti_web.map((f) => (
                        <li key={f.url} className="text-[11px] truncate"><a href={f.url} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">{f.title}</a> <span className="text-slate-600">{f.published_date?.slice(0, 10)}</span></li>
                      ))}
                    </ul>
                  )}
                  {lead.azienda_analysis.note && <p className="text-xs text-slate-400 mt-2 italic">{lead.azienda_analysis.note}</p>}
                  <p className="text-[10px] text-slate-600 mt-1">{lead.azienda_analysis.fonte}</p>
                </div>
              )}

              {/* CRIF */}
              {lead.crif_flags?.length > 0 && (
                <div className="rounded-lg bg-amber-950/40 border border-amber-900 p-4">
                  <div className="text-xs font-bold uppercase text-amber-400 mb-2">Segnalazioni CRIF</div>
                  <div className="flex flex-wrap gap-1.5">
                    {lead.crif_flags.map((c) => (
                      <span key={c} className="text-xs text-amber-300 bg-amber-950/60 border border-amber-800 rounded px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Motivo liquidità */}
              {lead.structured?.motivo_liquidita && (
                <div>
                  <div className="text-xs font-bold uppercase text-slate-400 mb-1">Motivo liquidità</div>
                  <p className="text-sm text-slate-300">{lead.structured.motivo_liquidita}</p>
                </div>
              )}

              {/* Tags */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-400 mb-2">Tag automatici</div>
                <div className="flex flex-wrap gap-1.5">
                  {lead.tags?.map((t) => (
                    <Badge key={t} variant="secondary" className="bg-slate-800 text-slate-300 font-mono text-[10px]">{t}</Badge>
                  ))}
                </div>
              </div>

              {/* Stage change */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-400 mb-2">Sposta in pipeline</div>
                <Select value={lead.pipeline_stage} onValueChange={changeStage}>
                  <SelectTrigger data-testid="detail-stage-select" className="bg-slate-900 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {pendingStage && (
                  <OutcomeDialog stage={pendingStage} leadName={lead.contatto?.nome || "lead"} onCancel={() => setPendingStage(null)}
                    onConfirm={(ef) => changeStage(pendingStage, ef)} />
                )}
              </div>

              {/* GDPR consenso */}
              {lead.gdpr_consent ? (
                <div data-testid="detail-gdpr" className="rounded-lg bg-emerald-950/30 border border-emerald-900 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    <div className="text-xs font-bold uppercase text-emerald-400">Consenso GDPR registrato</div>
                  </div>
                  <div className="grid grid-cols-2 gap-y-1 text-xs text-slate-300">
                    <span className="text-slate-500">Data/ora</span><span className="font-mono">{new Date(lead.gdpr_consent.timestamp).toLocaleString("it-IT")}</span>
                    <span className="text-slate-500">Versione</span><span>v{lead.gdpr_consent.versione}</span>
                    <span className="text-slate-500">IP</span><span className="font-mono">{lead.gdpr_consent.ip || "n.d."}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 italic leading-snug">{lead.gdpr_consent.testo}</p>
                </div>
              ) : (
                <div className="rounded-lg bg-slate-900 border border-white/10 p-3 text-xs text-slate-500">
                  Nessun consenso GDPR registrato per questo lead.
                </div>
              )}

              {/* Attività */}
              <LeadActivities leadId={leadId} onChanged={onUpdated} />

              {/* Note */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-400 mb-2">Note consulente</div>
                <Textarea data-testid="detail-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Aggiungi note interne..." className="bg-slate-900 border-white/10 text-white min-h-24" />
                <Button data-testid="detail-note-save" onClick={saveNote} size="sm" className="mt-2 gap-2 bg-accent hover:bg-accent/90 text-white">
                  <Save className="h-4 w-4" /> Salva nota
                </Button>
              </div>

              {/* History */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1">
                  <History className="h-3.5 w-3.5" /> Storico azioni
                </div>
                <div className="space-y-1.5">
                  {lead.history?.map((h) => (
                    <div key={`${h.ts}-${h.action}`} className="text-xs text-slate-400 flex gap-2">
                      <span className="font-mono text-slate-600">{new Date(h.ts).toLocaleString("it-IT")}</span>
                      <span>{h.action}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button data-testid="detail-delete-button" onClick={remove} variant="ghost"
                className="w-full gap-2 text-rose-400 hover:text-rose-300 hover:bg-rose-950/40">
                <Trash2 className="h-4 w-4" /> Elimina lead
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
